import { FarmBenchmarkFigures, FarmBenchmarkMetricId } from '../../models/farm-benchmark';
import { FarmBenchmarkStanding } from './colors';

/**
 * Listed literally, not built as `f-benchmark-metric-${id}`, so the translation check can see every key. A
 * metric the backend adds later falls back to its id rather than to a raw key.
 */
const METRIC_LABEL_KEYS: Record<FarmBenchmarkMetricId, string> = {
  BirthWeight: 'f-benchmark-metric-birth-weight',
  WeanedWeight: 'f-benchmark-metric-weaned-weight',
  NurseryGrowth: 'f-benchmark-metric-nursery-growth',
  FatteningGrowth: 'f-benchmark-metric-fattening-growth',
  Mortality: 'f-benchmark-metric-mortality',
  SlaughterWeight: 'f-benchmark-metric-slaughter-weight'
};

export const metricLabel = (metric: string, t: (key: string) => string): string => {
  const key = METRIC_LABEL_KEYS[metric as FarmBenchmarkMetricId];
  return key ? t(key) : metric;
};

/**
 * Decimals shown per unit. The API rounds everything to one decimal, which is noise on a gram figure in the
 * thousands and the whole difference on a carcass weight or a mortality rate.
 */
const UNIT_DECIMALS: Record<string, number> = { g: 0, 'g/day': 0, kg: 1, '%': 1 };

export const formatNumber = (value: number, unit: string): string => {
  const decimals = UNIT_DECIMALS[unit] ?? 1;
  return value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

export const formatValue = (value: number, unit: string): string =>
  unit === '%' ? `${formatNumber(value, unit)}%` : `${formatNumber(value, unit)} ${unit}`;

/** Signed, so "+30 g" and "-0.4%" say which side of the average without leaning on the colour. */
export const formatDelta = (value: number, unit: string): string => {
  const formatted = formatValue(Math.abs(value), unit);
  if (formatNumber(Math.abs(value), unit) === formatNumber(0, unit)) return formatted;
  return `${value > 0 ? '+' : '-'}${formatted}`;
};

/** Which side of the other farms' average the farm is on. Neutral when either side has no figure. */
export const standingOf = (figures: FarmBenchmarkFigures, lowerIsBetter: boolean): FarmBenchmarkStanding => {
  const { farmValue, othersAverage } = figures;
  if (farmValue === null || othersAverage === null || farmValue === othersAverage) return 'neutral';
  return farmValue < othersAverage === lowerIsBetter ? 'better' : 'worse';
};
